import type { CollateralKey } from "@/data/fixtures";
import { aggregatePrices } from "@/lib/oracle/price-aggregate";
import {
	COLLATERAL_KEYS,
	type ExternalPriceSamples,
} from "@/lib/oracle/price-providers";
import type { PriceFeed } from "@motokomaker/ic-client";

export const ORACLE_STALE_NS = 86_400_000_000_000n; // 24h

export type ChainPriceStatus =
	| "fresh"
	| "stale"
	| "pending_osm"
	| "missing"
	| "market_fallback";

export type ResolvedPriceFeed = PriceFeed & {
	oracleSource: string | null;
	status: ChainPriceStatus;
	sampleCount?: number;
};

export function isFeedStale(
	feed: PriceFeed,
	nowNs = BigInt(Date.now()) * 1_000_000n,
): boolean {
	if (feed.lastUpdated <= 0n) return true;
	const age = nowNs - feed.lastUpdated;
	return age > ORACLE_STALE_NS;
}

function isChainFeedUsable(feed: PriceFeed | undefined): boolean {
	return feed != null && feed.price > 0 && !isFeedStale(feed);
}

function feedStatus(feed: PriceFeed | undefined): ChainPriceStatus {
	if (feed == null || feed.price <= 0) return "missing";
	if (feed.pendingPrice != null && feed.pendingActivatesAt != null) {
		return "pending_osm";
	}
	if (isFeedStale(feed)) return "stale";
	return "fresh";
}

function aggregatedFeed(
	asset: CollateralKey,
	samples: number[],
): ResolvedPriceFeed | null {
	const price = aggregatePrices(samples);
	if (price == null) return null;
	const now = BigInt(Date.now()) * 1_000_000n;
	return {
		asset,
		price,
		lastUpdated: now,
		pendingPrice: null,
		pendingActivatesAt: null,
		source: "market_dev",
		providerCount: samples.length,
		oracleSource: "market_dev",
		status: "market_fallback",
		sampleCount: samples.length,
	};
}

/** Map on-chain protocol price feeds — play/production source of truth. */
export function resolveChainPriceFeeds(
	chainFeeds: PriceFeed[],
): ResolvedPriceFeed[] {
	const chainByAsset = new Map(chainFeeds.map((f) => [f.asset, f]));
	return COLLATERAL_KEYS.map((asset) => {
		const chain = chainByAsset.get(asset);
		if (chain) {
			return {
				...chain,
				oracleSource: chain.source ?? null,
				providerCount:
					chain.providerCount != null ? Number(chain.providerCount) : null,
				status: feedStatus(chain),
			};
		}
		return {
			asset,
			price: 0,
			lastUpdated: 0n,
			pendingPrice: null,
			pendingActivatesAt: null,
			source: null,
			providerCount: null,
			oracleSource: null,
			status: "missing" as const,
		};
	});
}

/** Local only: prefer fresh on-chain; fall back to aggregated external quotes. */
export function resolvePriceFeeds(
	chainFeeds: PriceFeed[],
	externalSamples: ExternalPriceSamples,
): ResolvedPriceFeed[] {
	const chainByAsset = new Map(chainFeeds.map((f) => [f.asset, f]));
	const resolved: ResolvedPriceFeed[] = [];

	for (const asset of COLLATERAL_KEYS) {
		const chain = chainByAsset.get(asset);
		if (chain && isChainFeedUsable(chain)) {
			resolved.push({
				...chain,
				oracleSource: chain.source ?? null,
				providerCount:
					chain.providerCount != null ? Number(chain.providerCount) : null,
				status: feedStatus(chain),
			});
			continue;
		}

		const samples = externalSamples[asset] ?? [];
		const fallback = aggregatedFeed(asset, samples);
		if (fallback) {
			resolved.push(fallback);
			continue;
		}

		if (chain && chain.price > 0) {
			resolved.push({
				...chain,
				oracleSource: chain.source ?? null,
				providerCount:
					chain.providerCount != null ? Number(chain.providerCount) : null,
				status: feedStatus(chain),
			});
		} else {
			resolved.push({
				asset,
				price: 0,
				lastUpdated: 0n,
				pendingPrice: null,
				pendingActivatesAt: null,
				source: null,
				providerCount: null,
				oracleSource: null,
				status: "missing",
			});
		}
	}

	return resolved;
}

/**
 * @param chainOnly — true for play + production (vault marks from protocol only).
 *                    false for local hybrid market_fallback.
 */
export function resolvePriceFeedsForEnv(
	chainFeeds: PriceFeed[],
	externalSamples: ExternalPriceSamples,
	chainOnly: boolean,
): ResolvedPriceFeed[] {
	if (chainOnly) {
		return resolveChainPriceFeeds(chainFeeds);
	}
	return resolvePriceFeeds(chainFeeds, externalSamples);
}
